import { Network } from '@wren/shared';
import type { PastDueInvoiceRecoveryResult } from '@wren/shared/dist/lib/stripe';
import type { PaymentIntent } from '@stripe/stripe-js';

type RecoveryError = { message?: string };

const RECOVERY_RETURN_PATHS = new Set([
  '/update-payment',
  '/update-team-payment',
]);

export function getRecoveryReturnDestination({
  returnPath,
  teamId,
  origin = window.location.origin,
}: {
  returnPath: string | null;
  teamId: string | null;
  origin?: string;
}) {
  const fallbackPath = teamId ? '/update-team-payment' : '/update-payment';
  const safePath =
    returnPath && RECOVERY_RETURN_PATHS.has(returnPath)
      ? returnPath
      : fallbackPath;
  return new URL(safePath, origin);
}

export type RedirectedRecoveryState =
  | 'not_applicable'
  | 'loading'
  | 'succeeded'
  | 'processing'
  | 'failed';

export function getRedirectedRecoveryState({
  clientSecret,
  retrieved,
  status,
}: {
  clientSecret: string | null;
  retrieved: boolean;
  status?: PaymentIntent['status'];
}): RedirectedRecoveryState {
  if (!clientSecret) return 'not_applicable';
  if (!retrieved) return 'loading';
  if (status === 'succeeded') return 'succeeded';
  if (status === 'processing') return 'processing';
  return 'failed';
}

export async function retryPastDueInvoice({
  setupIntentId,
  recoveryAttemptId,
  teamId,
}: {
  setupIntentId: string;
  recoveryAttemptId: string;
  teamId?: string;
}) {
  const [response, body] = await Network.post<
    PastDueInvoiceRecoveryResult | RecoveryError
  >('stripe/retry-past-due-invoice', {
    setupIntentId,
    recoveryAttemptId,
    ...(teamId ? { teamId } : {}),
  });

  if (!response.ok || !body || !('status' in body)) {
    throw new Error(
      body && 'message' in body && body.message
        ? body.message
        : 'We could not retry your payment. Please try again.'
    );
  }

  return body;
}
